import { AxisId, DebriefSeriesType } from 'ff/debrief/models';
import { getFormatter } from '.';
import { axisConfig } from 'ff/debrief/components/charts/axis';
import { axisId as axis } from 'ff/debrief/utils';
import { AxisConfig } from '@mui/x-charts';
import { ChartsYAxisProps } from '@mui/x-charts/models';
import moment from 'moment-timezone';

const baseSeriesConfig: DebriefSeriesType = {
    type: 'line',
    yAxisId: 'altitude',
    area: false,
    showMark: false,
};

export function createSeriesConfigure(axisId: AxisId, speedUnit?: string) {
    let seriesConfig: DebriefSeriesType;
    switch (axisId) {
        case 'altitude':
            seriesConfig = {
                ...baseSeriesConfig,
                yAxisId: axisId,
                id: axis.AltitudeAxis,
                color: axisConfig[axisId].color,
                area: true,
            };
            break;
        case 'terrain_elevation':
            seriesConfig = {
                ...baseSeriesConfig,
                yAxisId: axis.AltitudeAxis,
                id: axis.TerrainElevationAxis,
                color: axisConfig[axisId].color,
                area: true,
            };
            break;
        case 'above_ground_level':
            seriesConfig = {
                ...baseSeriesConfig,
                yAxisId: axisId,
                id: axis.AboveGroundLevelAxis,
                color: axisConfig[axisId].color,
                area: true,
            };
            break;
        case 'airspeed':
            seriesConfig = {
                ...baseSeriesConfig,
                yAxisId: axisId,
                id: axis.AirspeedAxis,
                color: axisConfig[axisId].color,
            };
            break;
        case 'bank':
            seriesConfig = {
                ...baseSeriesConfig,
                yAxisId: axisId,
                id: axis.BankAxis,
                color: axisConfig[axisId].color,
            };
            break;
        case 'g_load':
            seriesConfig = {
                ...baseSeriesConfig,
                yAxisId: axisId,
                id: axis.GLoadAxis,
                color: axisConfig[axisId].color,
            };
            break;
        case 'ground_speed':
            seriesConfig = {
                ...baseSeriesConfig,
                yAxisId: axisId,
                id: axis.GroundSpeedAxis,
                color: axisConfig[axisId].color,
            };
            break;
        case 'pitch':
            seriesConfig = {
                ...baseSeriesConfig,
                yAxisId: axisId,
                id: axis.PitchAxis,
                color: axisConfig[axisId].color,
            };
            break;
        default:
            throw new Error('no series config found for selected axis');
    }
    return (points: number[]): DebriefSeriesType => ({
        ...seriesConfig,
        valueFormatter: getFormatter(axisId, speedUnit),
        data: points,
    });
}

export function getTimeSeriesAxisConfig(data: number[]) {
    return {
        data: data,
        scaleType: 'time',
        tickNumber: 6,
        min: data.at(0),
        max: data.at(-1),
        valueFormatter: (value: number) => {
            if (value === null) {
                return '';
            }
            const date = new Date(value * 1000);
            if (date.getMinutes() === 0 && date.getSeconds() === 0) {
                return moment(date).format('h:mm A');
            }
            if (date.getMinutes() !== 0 && date.getSeconds() === 0) {
                return moment(date).format('h:mm');
            }
            if (date.getSeconds() !== 0) {
                return `:${date.getSeconds()}`;
            }
            // if not one of the above, the result is undefined, which will cause
            // mui to use the default formatter
        },
    } as const;
}

export function getYAxisConfig(axisId: AxisId, speedUnit?: string) {
    return (data: number[]): AxisConfig<'linear', number, ChartsYAxisProps> =>
        axisId === 'bank' || axisId === 'pitch'
            ? ({
                  id: axisId,
                  scaleType: 'linear',
                  width: 100,
                  valueFormatter: getFormatter(axisId, speedUnit),
                  ...getMinMax(data),
              } as const)
            : ({
                  id: axisId,
                  width: 100,
                  scaleType: 'linear',
                  valueFormatter: getFormatter(axisId, speedUnit),
              } as const);
}

export function getMinMax(nums: number[]): { min: number; max: number } {
    if (nums.length === 0) {
        throw new Error('Input array must not be empty.');
    }

    const computedMin = Math.min(...nums);
    const computedMax = Math.max(...nums);
    const defaultMin = -30;
    const defaultMax = 30;

    // If both values are within the default limits, return the default symmetric range.
    if (computedMin >= defaultMin && computedMax <= defaultMax) {
        return { min: defaultMin, max: defaultMax };
    }

    // Determine the symmetric value to use.
    let symmetricValue: number = 0;

    // If both computed values exceed their respective limits, choose the one with the larger deviation.
    if (computedMin < defaultMin && computedMax > defaultMax) {
        const deviationMin = Math.abs(defaultMin - computedMin); // how far below -30 computedMin is
        const deviationMax = Math.abs(computedMax - defaultMax); // how far above 30 computedMax is
        symmetricValue =
            deviationMin >= deviationMax ? Math.abs(computedMin) : computedMax;
    } else if (computedMin < defaultMin) {
        // Only the min exceeds its limit.
        symmetricValue = Math.abs(computedMin);
    } else if (computedMax > defaultMax) {
        // Only the max exceeds its limit.
        symmetricValue = computedMax;
    }

    return { min: -symmetricValue, max: symmetricValue };
}
